import { formatNumber } from '@trmediaab/zebra-utils';
import autobind from 'class-autobind';

import BetType from 'atg/api/BetType';
import DividendReduction from 'atg/api/DividendReduction';
import { DIVIDENDS, TIPPED_WINNERS } from 'atg/constants/BetUpdateType';
import * as Utils from 'atg/utils/Utils';
import StickyTemplate from 'atg/vendor/StickyTemplate/1.0/StickyTemplate';
import { settings } from 'shared/redux/selectors';
import { getStore } from 'shared/redux/store';
import { formatMoney } from 'shared/utils/money';
import * as Text from 'shared/utils/Text';

import { createHelpIcon } from '../HelpIcon';
import BaseView from './BaseView';

const $ = window.$;

function formatPredictedNumberOfWinnersValue(value) {
  const decimals = value > 1 && value < 10 ? 1 : 0;
  return formatNumber(value, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export default class DividendReductionView extends BaseView {
  constructor() {
    super(...arguments);
    autobind(this, DividendReductionView.prototype);
  }

  setup() {
    this.delegate.fetchInitialDividends(this.updateDividendReduction);
  }

  getGridClass() {
    return 'view-dividend-reduction';
  }

  // ---------------------------------------- MENU ----------------------------------------

  getViewTitle() {
    return Text.get('view-dividend-reduction');
  }

  getMenuTitle() {
    return Text.get('view-dividend-reduction');
  }

  getMenuTogglable() {
    return true;
  }

  getMenuDeletable() {
    return true;
  }

  // ---------------------------------------- GRID ----------------------------------------
  toggleItem() {
    this.getItem().toggleDisabled();
    this.getAPI().scheduleBetUpdate(DIVIDENDS);
  }

  deleteItem() {
    this.getItem().reset();
    this.getAPI().scheduleBetUpdate(DIVIDENDS);
  }

  getGridCellInfo(position, rowCache) {
    const api = this.getAPI();

    let race;
    let betRow;
    let gameRow;
    let resultRow;
    let stakeRow;
    let nrOfBetsRow;
    let betConfig;
    let dividendReduction;

    if (rowCache) {
      race = rowCache.race;
      betRow = rowCache.betRow;
      gameRow = rowCache.gameRow;
      resultRow = rowCache.resultRow;
      stakeRow = rowCache.stakeRow;
      nrOfBetsRow = rowCache.nrOfBetsRow;
      betConfig = rowCache.betConfig;
      dividendReduction = rowCache.dividendReduction;
    } else {
      // eslint-disable-next-line no-param-reassign
      rowCache = {};
      race = rowCache.race = api.raceInformation.getRace(position.row);
      betRow = rowCache.betRow = api.betInformation.getRow(position.row);
      gameRow = rowCache.gameRow = api.gameInformation.getRow(position.row);
      resultRow = rowCache.resultRow = api.resultInformation.getRow(
        position.row,
      );
      stakeRow = rowCache.stakeRow = api.stake.getRow(position.row);
      nrOfBetsRow = rowCache.nrOfBetsRow = api.stake.getNrOfBetsRow(
        position.row,
      );
      betConfig = rowCache.betConfig = api.betInformation.getBetConfig();
      dividendReduction = rowCache.dividendReduction =
        this.getDividendReduction();
    }

    const startNumber = betRow
      ? betRow.getStartNumberAtPosition(position.column)
      : position.column + 1;

    const startIndex = startNumber - 1;
    const start = race.getStart(startIndex);

    const info = {
      rowCache: rowCache,
      winner: dividendReduction.isDividendTippedWinner(
        position.row,
        start.getStartNumber(),
      ),
    };

    if (start) {
      info.number = start.getStartNumber();

      const horse = start.getHorse();
      if (horse) {
        info.horseName = horse.getName();
      }

      const jockey = start.getJockey();
      if (jockey) {
        info.jockeyTrainerName = jockey.getName();
      }

      const originalJockey = start.getOriginalJockey();
      if (originalJockey) {
        info.originalJockey = originalJockey.getName();
      }
    }

    if (
      resultRow &&
      settings.showRankingResults(getStore().getState()) &&
      betConfig.getShowResultRow(position.row)
    ) {
      info.winner = resultRow.isWinner(startIndex);
    }

    const gameRowCellInfo = this.getGameRowCellInfo(gameRow, startIndex);
    Object.assign(info, gameRowCellInfo);

    const bottomInfoParts = [];

    if (betRow) {
      const reserveNumber = betRow.getReserveNumber(startIndex);
      if (reserveNumber) {
        bottomInfoParts.push(
          `<strong class="font-extra-bold">R${reserveNumber}</strong>`,
        );
      }
    }

    if (stakeRow) {
      const percent = stakeRow.getPercentOfBets(position.column);
      if (percent) {
        const nrOfBets = nrOfBetsRow
          ? formatNumber(nrOfBetsRow.getNrOfBets(position.column))
          : undefined;
        bottomInfoParts.push(
          `${String(Math.round(percent * 1000) / 10)}%${
            nrOfBets ? ` - ${nrOfBets}` : ''
          }`,
        );
      }
    }

    info.bottomInfo =
      bottomInfoParts.length > 0
        ? $(`<span>${bottomInfoParts.join(', ')}</span>`)
        : '-';

    return info;
  }

  onGridCellTapped(position) {
    this.getGrid().highlightCell(position);

    const buttons = [
      {
        icon: 'button-last',
        onPress: this.onGridLastButtonPressed,
      },
      {
        icon: 'button-cup',
        onPress: this.onGridCupButtonPressed,
      },
      ...this.makeHorseButtons(position),
    ];

    this.getGrid().showCellButtons(position, buttons);
  }

  onGridCupButtonPressed(position) {
    const race = this.getAPI().raceInformation.getRace(position.row);
    const betRow = this.getAPI().betInformation.getRow(position.row);
    const startNumber = betRow.getStartNumberAtPosition(position.column);
    const startIndex = startNumber - 1;
    const start = race.getStart(startIndex);
    const dividendReduction = this.getDividendReduction();

    dividendReduction.setDividendTippedWinner(
      position.row,
      start.getStartNumber(),
    );
    this.getGrid().updateRow(position.row);
    this.getGrid().closeHighlight();
    this.updateDividendReduction();

    this.getAPI().scheduleBetUpdate(TIPPED_WINNERS);
  }

  onRowWasUpdated() {}

  requestDividendsInUpdate() {
    return true;
  }

  // ---------------------------------------- DATA ----------------------------------------

  getItem() {
    return this.getDividendReduction();
  }

  getDividendReduction() {
    let reduction = this.getAPI()
      .betInformation.getReductionSet()
      .getDividendReduction();

    if (!reduction) {
      reduction = new DividendReduction();
    }

    return reduction;
  }

  // ---------------------------------------- BOTTOM INFO DELEGATE METHODS ----------------------------------------

  getBottomInfoContent() {
    const $conditions = (this.$conditions = StickyTemplate.jq(
      'bottom-conditions-dividend-reduction',
      {
        '%dividend-reduction%': Text.get(
          'bottom-conditions-dividend-reduction',
        ),
        '%bottom-conditions-dividend-reduction-disclaimer-atg%': Text.get(
          'bottom-conditions-dividend-reduction-disclaimer-atg',
        ),
      },
    ));

    $conditions.find('form').event('submit', this.onFormSubmit);
    this.updateDividendReduction();

    return $conditions;
  }

  // ---------------------------------------- POINT SUMS ----------------------------------------

  updateDividendReduction() {
    const betConfig = this.getAPI().betInformation.getBetConfig();
    const dividendReduction = this.getDividendReduction();
    const dividend = this.getAPI().stake.getDividend();
    const latestTurnoverAt = this.getAPI().gameInformation.getTimestamp();
    const stakeBasedOnTurnoverAt = this.getAPI()
      .getStake()
      .getStakeAmountBasedOnTurnoverAt();

    let numRaces = this.getAPI().raceInformation.getNumberOfRaces();

    const $wrapper = this.$conditions.find('.dividend-reduction');
    const $rows = $wrapper.find('.rows');

    $rows.empty();
    //  this.$conditions.find('.allowed-errors')[1].checked = true;

    const jackpotLabelID = `bottom-conditions-dividend-reduction-jackpot-nr_${numRaces--}`;
    const oneErrorLabelID = `bottom-conditions-dividend-reduction-jackpot-nr_${numRaces--}`;
    const twoErrorLabelID = `bottom-conditions-dividend-reduction-jackpot-nr_${numRaces}`;

    const $row = StickyTemplate.jq(
      'bottom-conditions-dividend-reduction-inner',
      {
        '%winning-row%': Text.get(
          'bottom-conditions-dividend-reduction-winning-row',
        ),
        '%dividend-reduction-jackpot%': Text.get(jackpotLabelID),
        '%dividend-reduction-one-error%': Text.get(oneErrorLabelID),
        '%dividend-reduction-two-error%': Text.get(twoErrorLabelID),
        '%estimated-pot%': Text.get(
          'bottom-conditions-dividend-reduction-estimated-pot',
        ),

        '%estimated-count%': Text.get(
          'bottom-conditions-dividend-reduction-estimated-count',
        ),
        '%estimated-dividend-reduction%': Text.get(
          'bottom-conditions-dividend-reduction-estimated-dividend-reduction',
        ),
        '%estimated-nr-of-win-rows%': Text.get(
          'bottom-conditions-dividend-reduction-estimated-nr-of-winning-rows',
        ),
        '%estimated-system-win-jackpot%': Text.get(
          'bottom-conditions-dividend-reduction-estimated-system-win',
        ),

        '%condition%': `${Text.get(
          'bottom-conditions-dividend-reduction-condition',
        )}:`,
        '%v6condition%': Text.get(
          'bottom-conditions-dividend-v6-reduction-condition',
        ),
        '%v7condition%': Text.get(
          'bottom-conditions-dividend-v7-reduction-condition',
        ),
        '%min%': Text.get('bottom-conditions-dividend-reduction-min'),
        '%max%': Text.get('bottom-conditions-dividend-reduction-max'),
        '%v6-disabled-text%': Text.get(
          'bottom-conditions-dividend-v6-v7-reduction-disabled-text',
          'V6',
        ),
        '%v7-disabled-text%': Text.get(
          'bottom-conditions-dividend-v6-v7-reduction-disabled-text',
          'GS7',
        ),
        '%dividend-reduction-total%': Text.get(
          'bottom-conditions-dividend-reduction-total',
        ),
        '%target-win-header%': Text.get(
          'bottom-conditions-dividend-reduction-target-win',
        ),
        '%target-win-text%': Text.get(
          'bottom-conditions-dividend-reduction-target-win-text',
        ),
        '%possible-correct%':
          dividend != null
            ? Text.get(
                'bottom-conditions-dividend-reduction-possible-correct',
                dividend.getNumberOfWinningRows().reduce((acc, value, idx) => {
                  if (value > 0) {
                    return idx;
                  }

                  return acc;
                }, 0),
              )
            : '',
        '%button-update%': Text.get('button-update'),
        '%dividend-updated-at%':
          stakeBasedOnTurnoverAt != null
            ? Text.get(
                'bottom-conditions-dividend-updated-at',
                Utils.time(stakeBasedOnTurnoverAt, true),
              )
            : '',
      },
    );

    // If there are dividend requirements AND if they we're last calculated with an
    // outdated turnover, allow the user to update the calculation.
    if (
      !dividendReduction.isEmpty() &&
      stakeBasedOnTurnoverAt != null &&
      latestTurnoverAt != null &&
      stakeBasedOnTurnoverAt.getTime() < latestTurnoverAt.getTime()
    ) {
      if (betConfig.isShared()) {
        $row.find('.update-wrapper button').hide();
      } else {
        $row
          .find('.update-wrapper button')
          .reevent('click', () =>
            Utils.callDelegate(this.delegate, 'updateDividendCalculation'),
          );
      }
    } else {
      $row.find('.update-wrapper').hide();
    }

    const numRows = this.getAPI().betInformation.getNumberOfRows();

    for (let i = 0; i < numRows; i++) {
      const $input = StickyTemplate.jq(
        'bottom-conditions-dividend-reduction-winners',
        {
          '%row-number%': i,
        },
      );
      $input.val(dividendReduction.getDividendTippedWinner(i));

      $row.find('.container-winners').append($input);
    }

    $row.find('.container-winners').append(
      createHelpIcon({
        header: Text.get('help-dividend-reduction-winning-row-header'),
        body: Text.get('help-dividend-reduction-winning-row-body'),
      }),
    );

    $row.find('.play-expected-outcome').append(
      createHelpIcon({
        header: Text.get('help-dividend-reduction-expected-outcome-header'),
        body: Text.get('help-dividend-reduction-expected-outcome-body'),
      }),
    );

    $row.find('.show-if-v6-available-condition').append(
      createHelpIcon({
        header: Text.get(
          'help-dividend-reduction-expected-outcome-bettype-header',
        ),
        body: Text.get('help-dividend-reduction-expected-outcome-bettype-body'),
      }),
    );

    $row.find('.show-if-v7-available-condition').append(
      createHelpIcon({
        header: Text.get(
          'help-dividend-reduction-expected-outcome-bettype-header',
        ),
        body: Text.get('help-dividend-reduction-expected-outcome-bettype-body'),
      }),
    );

    $row.find('.target-win-header').append(
      createHelpIcon({
        header: Text.get('help-dividend-reduction-target-win-header'),
        body: Text.get('help-dividend-reduction-target-win-body'),
      }),
    );

    const $statsTable = $row.find('table.statistics');
    $statsTable.empty();

    const $predictedNumberOfWinners = $row.find('input.estimated-count');
    const $predictedDividend = $row.find('input.estimated-dividend-reduction');
    const $predictedSumOfStakes = $row.find('input.jackpot');

    const $nrOfWinningRows = $row.find('input.estimated-winning-rows');

    const $predictedSystemWin = $row.find('input.estimated-system-win');

    const $oneErrorRow = $row.find('.show-if-one-error-available');
    const $oneErrorRadioBtn = $row.find('.show-if-one-error-available-radio');
    const $predictedNumberOfOneError = $row.find(
      'input.estimated-count-one-error',
    );
    const $predictedDividendOneError = $row.find(
      'input.estimated-dividend-reduction-one-error',
    );
    const $predictedSumOfStakesOneError = $row.find('input.one-error');
    const $nrOfWinningRowsOneError = $row.find(
      'input.estimated-winning-rows-one-error',
    );
    const $predictedSystemWinOneError = $row.find(
      'input.estimated-system-win-one-error',
    );

    const $twoErrorRow = $row.find('.show-if-two-error-available');
    const $twoErrorRadioBtn = $row.find('.show-if-two-error-available-radio');
    const $predictedNumberOfTwoError = $row.find(
      'input.estimated-count-two-error',
    );
    const $predictedDividendTwoError = $row.find(
      'input.estimated-dividend-reduction-two-error',
    );
    const $predictedSumOfStakesTwoError = $row.find('input.two-error');
    const $nrOfWinningRowsTwoError = $row.find(
      'input.estimated-winning-rows-two-error',
    );
    const $predictedSystemWinTwoError = $row.find(
      'input.estimated-system-win-two-error',
    );

    const $predictedSystemWinTotal = $row.find(
      'input.estimated-system-win-total',
    );

    const $ifV6Section = $row.find('.show-if-v6-available');
    const $ifV7Section = $row.find('.show-if-v7-available');

    $predictedNumberOfWinners.val('');
    $predictedDividend.val('');
    $predictedSumOfStakes.val('');
    $nrOfWinningRows.val('');
    $predictedSystemWin.val('');

    $predictedNumberOfOneError.val('');
    $predictedDividendOneError.val('');
    $predictedSumOfStakesOneError.val('');
    $nrOfWinningRowsOneError.val('');
    $predictedSystemWinOneError.val('');

    $predictedNumberOfTwoError.val('');
    $predictedDividendTwoError.val('');
    $predictedSumOfStakesTwoError.val('');
    $nrOfWinningRowsTwoError.val('');
    $predictedSystemWinTwoError.val('');

    let total = 0;
    let value;
    let winDividend;

    if (dividend) {
      const intervals = dividend.data.dividendDistribution;
      if (intervals) {
        const numberOfIntervals = intervals.length;
        const totals = intervals.reduce(function (a, b) {
          return a + b;
        });

        const labels = [
          'dividend-interval-label-1-to-200',
          'dividend-interval-label-201-to-500',
          'dividend-interval-label-501-to-1000',
          'dividend-interval-label-1001-to-3000',
          'dividend-interval-label-3001-to-5000',
          'dividend-interval-label-5001-to-10000',
          'dividend-interval-label-10001-to-30000',
          'dividend-interval-label-30001-to-100000',
          'dividend-interval-label-100001-to-500000',
          'dividend-interval-label-over-500000',
          'dividend-interval-label-unplayed-rows',
        ];

        for (
          let intervalIndex = 0;
          intervalIndex < numberOfIntervals;
          intervalIndex++
        ) {
          const interval = intervals[intervalIndex];
          const label = Text.get(labels[intervalIndex]);
          const numberOfRows = interval;
          const percent = (interval / totals) * 100;

          const $tableRow = StickyTemplate.jq(
            'bottom-conditions-dividend-reduction-statistics-row',
            {
              '%col1%': label,
              '%col2%': numberOfRows,
              '%col3%': `${Math.round(percent * 10) / 10}%`,
              '%col4%': percent,
            },
          );

          $statsTable.append($tableRow);
        }
      }

      value = dividend.getPredictedNumberOfWinners();

      if (value) {
        $predictedNumberOfWinners.val(
          formatPredictedNumberOfWinnersValue(value),
        );
      }

      winDividend = value = dividend.getPredictedDividend();

      if (value) {
        $predictedDividend.val(
          formatMoney(value.getSum(), value.getCurrency()),
        );
      }

      value = dividend.getPredictedSumOfStakes();
      if (value) {
        $predictedSumOfStakes.val(
          formatMoney(value.getSum(), value.getCurrency()),
        );
      }

      const winningRows = dividend.getNumberOfWinningRows();
      let wR;

      if (winDividend) {
        wR = winningRows[winningRows.length - 1];
        const wD = winDividend.getSum() * wR;
        $nrOfWinningRows.val(wR ? wR : 0);
        $predictedSystemWin.val(
          formatMoney(wD ? wD : 0, winDividend.getCurrency()),
        );
        total += wD ? wD : 0;
      }

      if (dividend.getPredictedSumOfStakesOneError()) {
        value = dividend.getPredictedOneErrorNumberOfWinners();
        if (value) {
          $predictedNumberOfOneError.val(
            formatPredictedNumberOfWinnersValue(value),
          );
        }

        const oneErrorDividend = (value =
          dividend.getPredictedOneErrorDividend());
        if (value) {
          $predictedDividendOneError.val(
            formatMoney(value.getSum(), value.getCurrency()),
          );
        }

        value = dividend.getPredictedSumOfStakesOneError();
        if (value) {
          $predictedSumOfStakesOneError.val(
            formatMoney(value.getSum(), value.getCurrency()),
          );
        }

        if (oneErrorDividend) {
          wR = winningRows[winningRows.length - 2];
          const oED = oneErrorDividend.getSum() * wR;
          $nrOfWinningRowsOneError.val(wR ? wR : 0);
          $predictedSystemWinOneError.val(
            formatMoney(oED ? oED : 0, oneErrorDividend.getCurrency()),
          );
          total += oED ? oED : 0;
        }
      } else {
        $oneErrorRow.hide();
        $oneErrorRadioBtn.hide();
      }
      if (dividend.getPredictedSumOfStakesTwoError()) {
        value = dividend.getPredictedTwoErrorNumberOfWinners();
        if (value) {
          $predictedNumberOfTwoError.val(
            formatPredictedNumberOfWinnersValue(value),
          );
        }

        const twoErrorDividend = (value =
          dividend.getPredictedTwoErrorDividend());
        if (value) {
          $predictedDividendTwoError.val(
            formatMoney(value.getSum(), value.getCurrency()),
          );
        }

        value = dividend.getPredictedSumOfStakesTwoError();
        if (value) {
          $predictedSumOfStakesTwoError.val(
            formatMoney(value.getSum(), value.getCurrency()),
          );
        }

        if (twoErrorDividend) {
          wR = winningRows[winningRows.length - 3];
          const oED = twoErrorDividend.getSum() * wR;
          $nrOfWinningRowsTwoError.val(wR ? wR : 0);
          $predictedSystemWinTwoError.val(
            formatMoney(oED ? oED : 0, twoErrorDividend.getCurrency()),
          );
          total += oED ? oED : 0;
        }
      } else {
        $twoErrorRow.hide();
        $twoErrorRadioBtn.hide();
      }
    }

    $predictedSystemWinTotal.val(
      formatMoney(
        total,
        value
          ? value.getCurrency()
          : winDividend
            ? winDividend.getCurrency()
            : 'kr',
      ),
    );

    if (dividendReduction) {
      $row.find('.allowed-errors')[
        dividendReduction.getMaxAllowedErrors()
      ].checked = true;
      $row
        .find('.min-condition')
        .val(Utils.negativeBlankToValue(dividendReduction.getMinDividend()));
      $row
        .find('.max-condition')
        .val(Utils.negativeBlankToValue(dividendReduction.getMaxDividend()));
      $row
        .find('.min-v6-condition')
        .val(Utils.negativeBlankToValue(dividendReduction.getV6MinDividend()));
      $row
        .find('.max-v6-condition')
        .val(Utils.negativeBlankToValue(dividendReduction.getV6MaxDividend()));
      $row
        .find('.min-v7-condition')
        .val(Utils.negativeBlankToValue(dividendReduction.getV7MinDividend()));
      $row
        .find('.max-v7-condition')
        .val(Utils.negativeBlankToValue(dividendReduction.getV7MaxDividend()));

      $row
        .find('.target-win-condition')
        .val(Utils.negativeBlankToValue(dividendReduction.getTargetWin()));
    }

    const betKey = this.getAPI().betInformation.getBetKey();
    const betTypeName = betKey.getBetType();
    const betType = BetType.getBetType(betTypeName);

    // T, DD and LD have a limited Dividend Reduction.
    // They should only show Target Win.
    $row
      .find('.show-if-full-dividend-reduction')
      .toggle(!['T', 'DD', 'LD'].includes(betTypeName));

    $rows.append($row);

    this.getAPI().loadBetKeyInfo(betKey, betKeyInfo => {
      if (IS_NO_SITE || !betType.mayHaveV6() || !betKeyInfo.allowsAllIn()) {
        $ifV6Section.hide();
      } else {
        $ifV6Section.find('input').prop({ disabled: betConfig.getV6() });
        $ifV6Section.find('.v6-v7-disabled-text').toggle(betConfig.getV6());
      }
      if (IS_NO_SITE || !betType.mayHaveV7() || !betKeyInfo.allowsAllIn()) {
        $ifV7Section.hide();
      } else {
        $ifV7Section.find('input').prop({ disabled: betConfig.getV7() });
        $ifV7Section.find('.v6-v7-disabled-text').toggle(betConfig.getV7());
      }

      $wrapper
        .find('input[type=number]')
        .reevent('input', this.onConditionsChanged);
      $wrapper
        .find('input:not([type=number]), select')
        .reevent('change', this.onConditionsChanged);

      this.attachNegativeBlankEvents($wrapper);
    });
  }

  // ---------------------------------------- FORM TO DATA ----------------------------------------

  onConditionsChanged(event) {
    const $form = this.$conditions.find('form');
    const formData = $form.formData();
    const dividendReduction = this.getDividendReduction();

    const inputs = this.$conditions.find('.allowed-errors');
    let checked = 0;

    for (const input of inputs) {
      if (input.checked) {
        checked = Number.parseInt(input.value, 10);
      }
    }

    dividendReduction.setDividendTippedWinners(
      formData.dividendReduction.tippedWinners,
    );
    dividendReduction.setMaxAllowedErrors(checked);

    dividendReduction.setMinDividend(
      Utils.valueToNegativeBlank(formData.dividendReduction.minCondition),
    );
    dividendReduction.setMaxDividend(
      Utils.valueToNegativeBlank(formData.dividendReduction.maxCondition),
    );

    dividendReduction.setV6MinDividend(
      Utils.valueToNegativeBlank(formData.dividendReduction.minV6Condition),
    );
    dividendReduction.setV6MaxDividend(
      Utils.valueToNegativeBlank(formData.dividendReduction.maxV6Condition),
    );

    dividendReduction.setV7MinDividend(
      Utils.valueToNegativeBlank(formData.dividendReduction.minV7Condition),
    );
    dividendReduction.setV7MaxDividend(
      Utils.valueToNegativeBlank(formData.dividendReduction.maxV7Condition),
    );

    dividendReduction.setTargetWin(
      Utils.valueToNegativeBlank(formData.dividendReduction.targetWin),
    );

    if ($(event.currentTarget).data('row') != null) {
      this.getGrid().updateRow($(event.currentTarget).data('row'));
    }
    $(event.currentTarget).selected = true;
    // @API.js::1301
    this.getAPI().scheduleBetUpdate(DIVIDENDS);
    $(event.currentTarget).selected = true;
  }
}
